import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAvailableProviders,
  useCreateAiAgent,
  useProviderSchema,
} from "../../../hooks/useAiAgents";
import { useBilling } from "../../../hooks/useBilling";
import { PROVIDER_FEATURE_MAP } from "../../../lib/subscriptionFeatures";
import { useBillingStore } from "../../../stores/billingStore";
import {
  useToast,
  useFranchisesInfinite,
  usePostHog,
} from "@finitless/shared/hooks";
import { useIntercomContext } from "@finitless/shared/contexts";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";
import ProviderSettingsForm from "./ProviderSettingsForm";
import FranchiseSelect from "./FranchiseSelect";
import { Modal, Switch } from "../../ui";
import { Icon } from "../../ui/UIElements/Icon";
import { useUiLocale } from "../../../hooks/useUiLocale";
import { resolveSchemaTitle } from "../../../lib/aiAgentI18n";
import { getBrandIcon } from "@finitless/shared/lib";
import finitlessIsotype from "../../../assets/finitless-isotype.png";
import {
  normalizeSettingsLanguage,
  supportsManagedLanguage,
  LANGUAGE_OPTIONS,
  normalizeLanguageCode,
} from "../../../lib/aiAgentLanguage";

const WhatsAppIcon = () => (
  <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
);

const getProviderIcon = (provider) => {
  switch (provider) {
    case "whatsapp":
      return (
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500">
          <WhatsAppIcon />
        </div>
      );
    case "finitless":
      return (
        <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-700 bg-gray-800">
          {getBrandIcon(finitlessIsotype) && (
            <img
              src={getBrandIcon(finitlessIsotype)}
              alt="Finitless"
              className="h-8 w-8 object-contain"
            />
          )}
        </div>
      );
    default:
      return (
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-500">
          <svg
            className="h-6 w-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
      );
  }
};

const getProviderCardName = (provider, t) => {
  const key = `createModal.providerCards.${provider}.name`;
  const translated = t(key);
  // Fallback to capitalized provider name if key not found
  return translated !== key
    ? translated
    : provider.charAt(0).toUpperCase() + provider.slice(1);
};

const getProviderCardDescription = (provider, t) => {
  const key = `createModal.providerCards.${provider}.description`;
  const translated = t(key);
  return translated !== key
    ? translated
    : t("createModal.providerCards.default.description");
};

const CreateAgentModal = ({
  isOpen,
  onClose,
  onSuccess,
  onCreatedForOAuth,
}) => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [providerSettings, setProviderSettings] = useState({});
  const [selectedFranchiseId, setSelectedFranchiseId] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const navigate = useNavigate();

  const { data: availableProviders } = useAvailableProviders();
  const { features } = useBilling();
  const setShowUpgradeModal = useBillingStore(
    (state) => state.setShowUpgradeModal,
  );
  const {
    franchises,
    isLoading: franchisesLoading,
    isFetchingNextPage: franchisesFetchingNextPage,
    hasNextPage: franchisesHasNextPage,
    fetchNextPage: fetchNextFranchisesPage,
  } = useFranchisesInfinite({ enabled: !!selectedProvider });

  const { data: schema } = useProviderSchema(selectedProvider, selectedType);
  const createAgentMutation = useCreateAiAgent();
  const toast = useToast();
  const { trackActivation } = usePostHog();
  const { showMessage, isEnabled: isIntercomEnabled } = useIntercomContext();

  // Check if a provider is enabled in the subscription
  const isProviderEnabled = (provider) => {
    const featureKey = PROVIDER_FEATURE_MAP[provider];
    if (!featureKey) return true; // No gate for unknown providers
    return !!features[featureKey];
  };

  // Calculate required fields based on conditional schema (if/then/else)
  const getRequiredFields = () => {
    if (!schema) return [];

    const defaultAuthType = schema?.properties?.auth_type?.default || "manual";
    const authType = providerSettings?.auth_type || defaultAuthType;

    // Check for conditional required using if/then/else
    const ifCondition = schema.if;
    const thenRequired = schema.then?.required || [];
    const elseRequired = schema.else?.required || [];

    if (ifCondition?.properties) {
      const conditionField = Object.keys(ifCondition.properties)[0];
      const conditionValue = ifCondition.properties[conditionField]?.const;

      if (conditionField && conditionValue !== undefined) {
        return authType === conditionValue ? thenRequired : elseRequired;
      }
    }

    // Fallback to static required
    return schema.required || [];
  };

  // Validate all required fields before submission
  const validateRequiredFields = () => {
    const requiredFields = getRequiredFields();
    const missingFields = [];

    for (const field of requiredFields) {
      // Skip auth_type as it's always set
      if (field === "auth_type") continue;

      const value = providerSettings?.[field];
      const trimmedValue = typeof value === "string" ? value.trim() : value;

      if (!trimmedValue) {
        // Get field title from schema for better error message
        const fieldTitle = schema?.properties?.[field]?.title || field;
        missingFields.push(fieldTitle);
      }
    }

    return missingFields;
  };

  const getInitialSettings = (provider, schemaData) => {
    const base = supportsManagedLanguage(provider)
      ? normalizeSettingsLanguage(provider, {})
      : {};

    // Apply default values from schema
    if (schemaData?.properties) {
      for (const [key, prop] of Object.entries(schemaData.properties)) {
        if (prop.default !== undefined && base[key] === undefined) {
          base[key] = prop.default;
        }
      }
    }

    return base;
  };

  const handleProviderSelect = (provider) => {
    if (!isProviderEnabled(provider)) {
      handleClose();
      setShowUpgradeModal(true);
      return;
    }

    const providerData = availableProviders?.find(
      (p) => p.provider === provider,
    );
    const providerTypes = providerData?.types || [];
    const singleType = providerTypes.length === 1 ? providerTypes[0].type : "";

    setSelectedProvider(provider);
    setSelectedType(singleType);
    setProviderSettings(getInitialSettings(provider));
  };

  const handleBackToChannels = () => {
    setSelectedProvider("");
    setSelectedType("");
    setProviderSettings({});
    setSelectedFranchiseId("");
    setIsEnabled(true);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setProviderSettings(getInitialSettings(selectedProvider));
  };

  const handleSettingsChange = (settings) => {
    setProviderSettings(
      supportsManagedLanguage(selectedProvider)
        ? normalizeSettingsLanguage(selectedProvider, settings)
        : settings,
    );
  };

  // Apply schema defaults when schema loads
  useEffect(() => {
    if (schema?.properties) {
      setProviderSettings((prev) => {
        const updated = { ...prev };
        let hasChanges = false;

        for (const [key, prop] of Object.entries(schema.properties)) {
          if (prop.default !== undefined && updated[key] === undefined) {
            updated[key] = prop.default;
            hasChanges = true;
          }
        }

        return hasChanges ? updated : prev;
      });
    }
  }, [schema]);

  const handleFranchiseSelect = (franchiseId) => {
    setSelectedFranchiseId(franchiseId);
  };

  // Check if using business_login auth type
  const isBusinessLogin = providerSettings?.auth_type === "business_login";
  const isWhatsApp = selectedProvider === "whatsapp";
  const showLanguageInModal = isWhatsApp && isBusinessLogin;

  // Handle language code change
  const handleLanguageChange = (e) => {
    const normalizedValue = normalizeLanguageCode(e.target.value);
    setProviderSettings((prev) => ({
      ...prev,
      language_code: normalizedValue,
    }));
  };

  // Build agent data for creation
  const buildAgentData = () => {
    const cleanProviderSettings = supportsManagedLanguage(selectedProvider)
      ? normalizeSettingsLanguage(selectedProvider, providerSettings)
      : { ...(providerSettings || {}) };
    delete cleanProviderSettings.provider_external_id;

    const agentData = {
      provider: selectedProvider,
      provider_type: selectedType,
      provider_settings: cleanProviderSettings,
      franchise_id: parseInt(selectedFranchiseId),
      enabled: isEnabled,
    };

    // For manual auth, include provider_external_id
    if (selectedProvider === "whatsapp" && !isBusinessLogin) {
      const rawExternalId = providerSettings?.provider_external_id;
      const trimmedExternalId =
        typeof rawExternalId === "string"
          ? rawExternalId.trim()
          : rawExternalId;
      if (trimmedExternalId) {
        agentData.provider_external_id = trimmedExternalId;
      }
    }

    return agentData;
  };

  // Create agent for OAuth flow - creates agent then opens edit modal for OAuth
  const handleCreateAgentForOAuth = async () => {
    if (!selectedProvider || !selectedType || !selectedFranchiseId) {
      toast.warning(t("createModal.validation.missingInformationMessage"), {
        title: t("createModal.validation.missingInformationTitle"),
      });
      return;
    }

    // For business_login, language_code is required
    if (!providerSettings?.language_code) {
      toast.warning(t("createModal.validation.missingLanguageMessage"), {
        title: t("createModal.validation.missingLanguageTitle"),
      });
      return;
    }

    const agentData = buildAgentData();

    try {
      const createdAgent = await createAgentMutation.mutateAsync(agentData);

      // Reset form
      setSelectedProvider("");
      setSelectedType("");
      setProviderSettings({});
      setSelectedFranchiseId("");
      setIsEnabled(true);

      // Open edit modal with auto-connect OAuth
      onCreatedForOAuth?.(createdAgent);
    } catch (error) {
      console.error("Error creating agent:", error);
      handleApiError(error, toast, {
        title: t("createModal.toasts.createErrorTitle"),
        defaultMessage: t("createModal.toasts.createErrorMessage"),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProvider || !selectedType || !selectedFranchiseId) {
      toast.warning(t("createModal.validation.missingInformationMessage"), {
        title: t("createModal.validation.missingInformationTitle"),
      });
      return;
    }

    // For business_login, the OAuth flow handles creation
    if (selectedProvider === "whatsapp" && isBusinessLogin) {
      // User should click "Connect with WhatsApp Business" button instead
      return;
    }

    // Validate all required fields from schema
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      toast.warning(
        t("createModal.validation.missingRequiredFieldsMessage", {
          fields: missingFields.join(", "),
        }),
        {
          title: t("createModal.validation.missingRequiredFieldsTitle"),
        },
      );
      return;
    }

    // For WhatsApp with manual auth, also require provider_external_id
    if (selectedProvider === "whatsapp" && !isBusinessLogin) {
      const rawExternalId = providerSettings?.provider_external_id;
      const trimmedExternalId =
        typeof rawExternalId === "string"
          ? rawExternalId.trim()
          : rawExternalId;
      if (!trimmedExternalId) {
        toast.warning(t("createModal.validation.missingExternalIdMessage"), {
          title: t("createModal.validation.missingExternalIdTitle"),
        });
        return;
      }
    }

    const agentData = buildAgentData();

    try {
      const createdAgent = await createAgentMutation.mutateAsync(agentData);

      // Track WhatsApp connection if it's a WhatsApp agent with manual auth
      const trimmedExternalId =
        providerSettings?.provider_external_id?.trim?.();
      if (
        selectedProvider === "whatsapp" &&
        trimmedExternalId &&
        createdAgent?.id
      ) {
        trackActivation.whatsappConnected({
          agent_id: createdAgent.id,
          provider_external_id: trimmedExternalId,
          franchise_id: parseInt(selectedFranchiseId),
        });
      }

      handleApiSuccess(t("createModal.toasts.createSuccess"), toast, {
        title: t("common.success"),
      });
      onSuccess?.();
      onClose();
      // Reset form
      setSelectedProvider("");
      setSelectedType("");
      setProviderSettings({});
      setSelectedFranchiseId("");
      setIsEnabled(true);
    } catch (error) {
      console.error("Error creating agent:", error);
      handleApiError(error, toast, {
        title: t("createModal.toasts.createErrorTitle"),
        defaultMessage: t("createModal.toasts.createErrorMessage"),
      });
    }
  };

  const handleClose = () => {
    setSelectedProvider("");
    setSelectedType("");
    setProviderSettings({});
    setSelectedFranchiseId("");
    setIsEnabled(true);
    onClose();
  };

  const selectedProviderData = availableProviders?.find(
    (p) => p.provider === selectedProvider,
  );
  const shouldShowTypeSelect =
    selectedProvider && (selectedProviderData?.types?.length || 0) > 1;

  const handleGoToBrands = () => {
    handleClose();
    navigate("/menu/brands?create=true");
  };

  // Show channel selection cards when no provider is selected
  const showChannelSelection = !selectedProvider;

  const showNoFranchisesEmptyState =
    !franchisesLoading && franchises.length === 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t("createModal.title")}
        subtitle={t("createModal.subtitle")}
        maxWidth="max-w-2xl"
      >
        {showChannelSelection ? (
          /* Channel Selection Cards */
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-300">
              {t("createModal.labels.provider")}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {availableProviders?.map((provider) => {
                const enabled = isProviderEnabled(provider.provider);
                return (
                  <button
                    key={provider.provider}
                    type="button"
                    onClick={() => handleProviderSelect(provider.provider)}
                    className={`relative flex items-start gap-4 rounded-xl border p-4 text-left transition-all ${
                      enabled
                        ? "cursor-pointer border-gray-700 bg-gray-800/50 hover:border-purple-500/50 hover:bg-gray-800"
                        : "cursor-pointer border-gray-700/50 bg-gray-900/50 hover:border-purple-500/30"
                    }`}
                    data-testid={`provider-card-${provider.provider}`}
                  >
                    {/* Provider icon */}
                    <div
                      className={`flex-shrink-0 ${!enabled ? "opacity-50" : ""}`}
                    >
                      {getProviderIcon(provider.provider)}
                    </div>

                    {/* Provider info */}
                    <div className="min-w-0 flex-1 pt-0.5">
                      <h4
                        className={`text-sm font-semibold ${enabled ? "text-white" : "text-gray-400"}`}
                      >
                        {getProviderCardName(provider.provider, t)}
                      </h4>
                      <p
                        className={`mt-1 text-xs leading-relaxed ${enabled ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {getProviderCardDescription(provider.provider, t)}
                      </p>
                      {!enabled && (
                        <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm shadow-purple-600/25 transition-colors hover:bg-purple-500">
                          <Icon name="Zap" className="h-3 w-3" />
                          {t("createModal.providerCards.upgrade")}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Cancel button */}
            <div className="flex border-t border-gray-700 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 rounded-lg bg-gray-600 px-4 py-3 font-medium text-white transition-colors hover:bg-gray-700"
              >
                {t("actions.cancel")}
              </button>
            </div>
          </div>
        ) : (
          /* Agent Configuration Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Back to channel selection */}
            <button
              type="button"
              onClick={handleBackToChannels}
              className="inline-flex items-center gap-1 text-sm text-gray-400 transition-colors hover:text-white"
            >
              <Icon name="ArrowLeft" className="h-4 w-4" />
              {t("createModal.providerCards.backToChannels")}
            </button>

            {/* Selected provider indicator */}
            <div className="flex items-center gap-3 rounded-lg border border-gray-700 bg-gray-800/50 p-3">
              {getProviderIcon(selectedProvider)}
              <div>
                <h4 className="text-sm font-semibold text-white">
                  {getProviderCardName(selectedProvider, t)}
                </h4>
                <p className="text-xs text-gray-400">
                  {getProviderCardDescription(selectedProvider, t)}
                </p>
              </div>
            </div>

            {/* Type Selection */}
            {shouldShowTypeSelect && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {t("createModal.labels.type")}
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="custom-select w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  required
                >
                  <option value="">
                    {t("createModal.placeholders.selectType")}
                  </option>
                  {selectedProviderData?.types?.map((type) => (
                    <option key={type.type} value={type.type}>
                      {resolveSchemaTitle({
                        title: type.title,
                        provider: selectedProvider,
                        type: type.type,
                        t,
                        locale,
                      })}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Brand, Enable Agent, and Language Code (for WhatsApp business_login) */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {t("createModal.labels.franchise")}
                </label>
                {showNoFranchisesEmptyState ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center rounded-md border border-gray-600 bg-gray-800 p-4">
                      <div className="text-center">
                        <Icon
                          name="AlertCircle"
                          className="mx-auto mb-2 h-8 w-8 text-yellow-500"
                        />
                        <p className="mb-3 text-sm text-gray-400">
                          {t("createModal.empty.noFranchises")}
                        </p>
                        <button
                          type="button"
                          onClick={handleGoToBrands}
                          className="inline-flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                        >
                          <Icon name="Plus" className="h-4 w-4" />
                          <span>{t("createModal.actions.goToBrands")}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <FranchiseSelect
                    value={selectedFranchiseId}
                    onChange={handleFranchiseSelect}
                    disabled={franchisesLoading}
                    placeholder={t("createModal.placeholders.selectFranchise")}
                    placeholderLoading={t(
                      "createModal.placeholders.loadingFranchises",
                    )}
                    loadingMoreLabel={t(
                      "createModal.placeholders.loadingMoreFranchises",
                    )}
                    franchises={franchises}
                    isLoading={franchisesLoading}
                    isFetchingNextPage={franchisesFetchingNextPage}
                    hasNextPage={franchisesHasNextPage}
                    fetchNextPage={fetchNextFranchisesPage}
                    disabledSuffix={t("common.disabledSuffix")}
                    required
                    aria-label={t("createModal.labels.franchise")}
                  />
                )}
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">
                  {t("createModal.labels.enableAgent")}
                </label>
                <div className="flex h-10 items-center rounded-md border border-gray-600 bg-gray-800 px-3 py-2">
                  <Switch
                    id="enabled"
                    checked={isEnabled}
                    onCheckedChange={setIsEnabled}
                  />
                </div>
              </div>

              {/* Language Code - shown in modal for WhatsApp business_login */}
              {showLanguageInModal && (
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    {t("settingsForm.languageTitle")}
                    <span className="ml-1 text-red-400">*</span>
                  </label>
                  <select
                    value={normalizeLanguageCode(
                      providerSettings?.language_code || "",
                    )}
                    onChange={handleLanguageChange}
                    className="custom-select w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`settingsForm.languageOptions.${option.value}`)}
                      </option>
                    ))}
                  </select>
                  {isIntercomEnabled && (
                    <button
                      type="button"
                      onClick={() =>
                        showMessage(
                          t("settingsForm.newLanguageIntercomPrefill"),
                        )
                      }
                      className="mt-1 text-sm text-blue-400 transition-colors hover:text-blue-300 hover:underline"
                    >
                      {t("settingsForm.newLanguageCta")}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Provider Settings Form */}
            {selectedProvider && selectedType && (
              <div>
                <ProviderSettingsForm
                  provider={selectedProvider}
                  type={selectedType}
                  initialValues={providerSettings}
                  onChange={handleSettingsChange}
                  readOnly={false}
                  onCreateAgent={handleCreateAgentForOAuth}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 border-t border-gray-700 pt-6">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 rounded-lg bg-gray-600 px-4 py-3 font-medium text-white transition-colors hover:bg-gray-700"
              >
                {t("actions.cancel")}
              </button>
              <button
                type="submit"
                disabled={
                  createAgentMutation.isPending ||
                  !selectedProvider ||
                  !selectedType ||
                  !selectedFranchiseId
                }
                className="flex-1 rounded-lg bg-purple-600 px-4 py-3 font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-600"
              >
                {createAgentMutation.isPending
                  ? t("createModal.actions.creating")
                  : t("createModal.actions.createAgent")}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default CreateAgentModal;
